import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  HStack,
  Text,
  Icon,
  Spinner,
  Kbd,
  Badge,
  useColorModeValue,
  Portal,
  Divider,
  Tabs,
  TabList,
  Tab,
  Tooltip,
} from '@chakra-ui/react';
import { FiSearch, FiX, FiUser, FiBook, FiFileText, FiFolder, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildHighlightedNodes } from '../utils/highlightUtils';
import { useGlobalSearch, SearchResultItem, SearchTabType } from '../hooks/useGlobalSearch';

interface GlobalSearchBarProps {
  placeholder?: string;
  autoFocus?: boolean;
  onResultSelect?: (result: SearchResultItem) => void;
  showShortcut?: boolean;
  size?: 'sm' | 'md' | 'lg';
  maxWidth?: string;
}

// Icon mapping based on entity type
const getEntityIcon = (entityType: string, iconType?: string) => {
  if (iconType) {
    switch (iconType.toLowerCase()) {
      case 'user':
        return FiUser;
      case 'classroom':
        return FiBook;
      case 'assignment':
      case 'exercise_template':
        return FiFileText;
      default:
        return FiFolder;
    }
  }

  switch (entityType) {
    case 'USER':
      return FiUser;
    case 'CLASSROOM':
      return FiBook;
    case 'ASSIGNMENT':
    case 'EXERCISE_TEMPLATE':
      return FiFileText;
    default:
      return FiFolder;
  }
};

// Get badge color based on entity type
const getEntityBadgeColor = (entityType: string) => {
  switch (entityType) {
    case 'USER':
      return 'blue';
    case 'CLASSROOM':
      return 'green';
    case 'ASSIGNMENT':
      return 'purple';
    case 'EXERCISE_TEMPLATE':
      return 'orange';
    default:
      return 'gray';
  }
};

// Get entity type display name
const getEntityTypeName = (entityType: string, t: (key: string) => string) => {
  switch (entityType) {
    case 'USER':
      return t('search.entityType.user');
    case 'CLASSROOM':
      return t('search.entityType.classroom');
    case 'ASSIGNMENT':
      return t('search.entityType.assignment');
    case 'EXERCISE_TEMPLATE':
      return t('search.entityType.template');
    default:
      return entityType;
  }
};

// Metadata helpers
const formatDateTime = (value?: unknown) => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const MAX_USER_CLASSROOMS_CHARS = 80;

const normalizeClassroomName = (value: unknown) => {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const candidate =
      record.name ??
      record.title ??
      record.classroomName ??
      record.classroom_name ??
      record.classroom_code ??
      record.code;
    return typeof candidate === 'string' ? candidate.trim() : null;
  }
  return null;
};

const getUserClassroomsLabel = (result: SearchResultItem) => {
  if (result.entityType !== 'USER') return null;
  const extra = (result.extraData ?? {}) as Record<string, unknown>;
  const raw =
    extra.classrooms ??
    extra.classroomNames ??
    extra.classroom_names ??
    extra.joinedClassrooms ??
    extra.joined_classrooms ??
    extra.classes ??
    extra.class_list ??
    extra.classList ??
    extra.classroom_list ??
    extra.classroomList;

  let names: string[] = [];
  if (Array.isArray(raw)) {
    names = raw.map(normalizeClassroomName).filter((item): item is string => Boolean(item));
  } else if (typeof raw === 'string') {
    names = raw
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (!names.length && extra.classroom) {
    const single = normalizeClassroomName(extra.classroom);
    if (single) names = [single];
  }

  if (!names.length) return null;

  const label = names.join(', ');
  if (label.length > MAX_USER_CLASSROOMS_CHARS) {
    return `${label.slice(0, MAX_USER_CLASSROOMS_CHARS).trimEnd()}…`;
  }
  return label;
};

const getResultMetadata = (result: SearchResultItem) => {
  const extra = (result.extraData ?? {}) as Record<string, unknown>;
  const userClassrooms = getUserClassroomsLabel(result);
  if (userClassrooms) {
    return userClassrooms;
  }
  if (result.entityType === 'USER') {
    return null;
  }
  const createdAt = formatDateTime(
    result.createdAt ??
      extra.createdAt ??
      extra.created_at ??
      extra.createdAtText ??
      extra.createdAtIso ??
      extra.created_at_iso ??
      extra.createdAtISO ??
      extra.created_at_ISO,
  );
  const createdBy = (extra.created_by_name ??
    extra.created_by_full_name ??
    extra.created_by ??
    extra.created_by_id ??
    result.createdByName ??
    result.createdBy ??
    extra.createdByName ??
    extra.createdBy ??
    extra.createdByFullName) as string | undefined;

  const kind = extra.kind ? String(extra.kind) : null;
  const level = extra.level ? String(extra.level) : null;
  const code = result.entityCode ? String(result.entityCode) : null;

  const metaParts = [createdBy, createdAt, kind, level, code].filter(Boolean) as string[];
  if (!metaParts.length) return null;
  return metaParts.join(' • ');
};

// Render highlighted text with <mark> tags (server or client)
const HighlightedText: React.FC<{ html?: string; text: string; query?: string }> = ({
  html,
  text,
  query,
}) => {
  if (html && html.includes('<mark>')) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: html.replace(
            /<mark>/g,
            '<mark style="background-color: #F6E05E; padding: 0 2px;">',
          ),
        }}
      />
    );
  }

  return <>{buildHighlightedNodes(text, query)}</>;
};

export default function GlobalSearchBar({
  placeholder,
  autoFocus = false,
  onResultSelect,
  showShortcut = true,
  size = 'md',
  maxWidth = '600px',
}: GlobalSearchBarProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const ignoreBlurRef = useRef(false);
  const {
    query,
    debouncedQuery,
    selectedType,
    results,
    totalCount,
    effectiveTypeCounts,
    allCount,
    isLoading,
    isFetching,
    hasMore,
    updateQuery,
    clearSearchState,
    loadMore,
    restoreFromCache,
    selectType,
    setSelectedType,
  } = useGlobalSearch({ pageSize: 10 });

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const itemIconBg = useColorModeValue('gray.100', 'gray.700');

  const hasResults = results.length > 0;
  const showPopover = isFocused;

  const tabItems = useMemo(() => {
    const items = [
      {
        type: 'ALL' as SearchTabType,
        label: t('search.all', { defaultValue: 'Tất cả' }),
        count: allCount,
      },
      {
        type: 'USER' as SearchTabType,
        label: t('search.entityType.user'),
        count: effectiveTypeCounts?.USER ?? 0,
      },
      {
        type: 'CLASSROOM' as SearchTabType,
        label: t('search.entityType.classroom'),
        count: effectiveTypeCounts?.CLASSROOM ?? 0,
      },
      {
        type: 'ASSIGNMENT' as SearchTabType,
        label: t('search.entityType.assignment'),
        count: effectiveTypeCounts?.ASSIGNMENT ?? 0,
      },
      {
        type: 'EXERCISE_TEMPLATE' as SearchTabType,
        label: t('search.entityType.template'),
        count: effectiveTypeCounts?.EXERCISE_TEMPLATE ?? 0,
      },
    ];
    return items.filter((item, index) => index === 0 || item.count > 0);
  }, [t, allCount, effectiveTypeCounts]);

  const activeTabIndex = Math.max(
    0,
    tabItems.findIndex((item) => item.type === selectedType),
  );

  useEffect(() => {
    if (!tabItems.find((item) => item.type === selectedType)) {
      setSelectedType('ALL');
    }
  }, [tabItems, selectedType]);

  // Handle result selection
  const handleSelect = useCallback(
    (result: SearchResultItem) => {
      navigate(result.navigateTo);
      if (onResultSelect) {
        onResultSelect(result);
      }
      updateQuery('');
      setIsFocused(false);
    },
    [navigate, onResultSelect],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showPopover || !hasResults) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            handleSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsFocused(false);
          inputRef.current?.blur();
          break;
      }
    },
    [showPopover, hasResults, results, selectedIndex, handleSelect],
  );

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const items = resultsRef.current.querySelectorAll('[data-search-result="true"]');
      const selectedElement = items[selectedIndex] as HTMLElement | undefined;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Global keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (ignoreBlurRef.current) {
      event.preventDefault();
      inputRef.current?.focus();
      return;
    }
    const nextFocused = event.relatedTarget as HTMLElement | null;
    window.setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (
        activeElement &&
        (activeElement === inputRef.current || popoverRef.current?.contains(activeElement))
      ) {
        return;
      }
      if (
        nextFocused &&
        (nextFocused === inputRef.current || popoverRef.current?.contains(nextFocused))
      ) {
        return;
      }
      setIsFocused(false);
    }, 0);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    restoreFromCache();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateQuery(e.target.value);
  };

  const handleClear = () => {
    clearSearchState();
    setIsFocused(false);
    inputRef.current?.focus();
  };

  const handleLoadMore = () => {
    loadMore();
  };

  const inputSize = {
    sm: { h: '32px', fontSize: 'sm' },
    md: { h: '40px', fontSize: 'md' },
    lg: { h: '48px', fontSize: 'lg' },
  }[size];

  return (
    <Box position="relative" maxW={maxWidth} w="100%">
      <Popover
        isOpen={showPopover}
        onClose={() => {
          const activeElement = document.activeElement as HTMLElement | null;
          if (
            activeElement &&
            (activeElement === inputRef.current || popoverRef.current?.contains(activeElement))
          ) {
            return;
          }
          setIsFocused(false);
        }}
        closeOnBlur={false}
        placement="bottom-start"
        autoFocus={false}
        matchWidth
        isLazy
      >
        <PopoverTrigger>
          <InputGroup size={size}>
            <InputLeftElement pointerEvents="none" h={inputSize.h}>
              {isLoading || isFetching ? (
                <Spinner size="sm" color="gray.400" />
              ) : (
                <Icon as={FiSearch} color="gray.400" />
              )}
            </InputLeftElement>

            <Input
              ref={inputRef}
              value={query}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onMouseDown={() => {
                ignoreBlurRef.current = true;
                setIsFocused(true);
              }}
              onMouseUp={() => {
                ignoreBlurRef.current = false;
              }}
              onMouseLeave={() => {
                ignoreBlurRef.current = false;
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t('search.placeholder')}
              bg={bgColor}
              borderColor={borderColor}
              autoFocus={autoFocus}
              h={inputSize.h}
              fontSize={inputSize.fontSize}
              pr={showShortcut ? '80px' : '40px'}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
            />

            <InputRightElement h={inputSize.h} w="auto" pr={2}>
              <HStack spacing={1}>
                {query && (
                  <Box
                    as="button"
                    onClick={handleClear}
                    p={1}
                    borderRadius="md"
                    _hover={{ bg: hoverBg }}
                  >
                    <Icon as={FiX} color="gray.400" boxSize={4} />
                  </Box>
                )}
                {showShortcut && !query && (
                  <HStack spacing={0.5}>
                    <Kbd fontSize="xs">⌘</Kbd>
                    <Kbd fontSize="xs">K</Kbd>
                  </HStack>
                )}
              </HStack>
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>

        <Portal>
          <PopoverContent
            w="100%"
            maxW={maxWidth}
            maxH="400px"
            overflowY="auto"
            bg={bgColor}
            borderColor={borderColor}
            shadow="lg"
            _focus={{ outline: 'none' }}
            ref={popoverRef}
            onMouseDown={() => {
              ignoreBlurRef.current = true;
            }}
            onMouseUp={() => {
              ignoreBlurRef.current = false;
            }}
            onMouseLeave={() => {
              ignoreBlurRef.current = false;
            }}
          >
            <PopoverBody p={0}>
              {debouncedQuery.length >= 2 && allCount > 0 && (
                <Box px={3} py={2} borderBottom="1px" borderColor={borderColor}>
                  <Tabs
                    size="sm"
                    variant="soft-rounded"
                    index={activeTabIndex}
                    onChange={(index) => {
                      const nextType = tabItems[index]?.type ?? 'ALL';
                      selectType(nextType);
                    }}
                  >
                    <TabList gap={1} flexWrap="wrap">
                      {tabItems.map((tab) => (
                        <Tab key={tab.type}>
                          {tab.label} ({tab.count})
                        </Tab>
                      ))}
                    </TabList>
                  </Tabs>
                </Box>
              )}

              {!hasResults && !isLoading && debouncedQuery.length >= 2 && (
                <Box p={4} textAlign="center">
                  <Text color="gray.500">{t('search.noResults')}</Text>
                </Box>
              )}

              {hasResults && (
                <Box ref={resultsRef}>
                  <VStack align="stretch" spacing={0} divider={<Divider />}>
                    {results.map((result, index) => {
                      const metadataLine = getResultMetadata(result);
                      const tooltipLabel = (
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">
                            {result.title}
                          </Text>
                          {result.description && (
                            <Text fontSize="xs" mt={1}>
                              {result.description}
                            </Text>
                          )}
                          {result.contentPreview && !result.description && (
                            <Text fontSize="xs" mt={1}>
                              {result.contentPreview}
                            </Text>
                          )}
                          {metadataLine && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {metadataLine}
                            </Text>
                          )}
                        </Box>
                      );

                      return (
                        <Tooltip
                          key={`${result.entityType}-${result.entityId}-${index}`}
                          label={tooltipLabel}
                          placement="right"
                          hasArrow
                          openDelay={600}
                          shouldWrapChildren
                        >
                          <Box
                            px={3}
                            py={2}
                            cursor="pointer"
                            bg={index === selectedIndex ? selectedBg : 'transparent'}
                            _hover={{ bg: index === selectedIndex ? selectedBg : hoverBg }}
                            onClick={() => handleSelect(result)}
                            role="option"
                            aria-selected={index === selectedIndex}
                            data-search-result="true"
                          >
                            <HStack spacing={3} align="start">
                              <Box p={2} borderRadius="md" bg={itemIconBg}>
                                <Icon
                                  as={getEntityIcon(result.entityType, result.iconType)}
                                  boxSize={4}
                                  color={`${getEntityBadgeColor(result.entityType)}.500`}
                                />
                              </Box>

                              <Box flex={1} minW={0}>
                                <HStack spacing={2} mb={0.5} align="start">
                                  <Text
                                    fontWeight="semibold"
                                    fontSize="xs"
                                    lineHeight="1.2"
                                    maxH="2.4em"
                                    overflow="hidden"
                                    whiteSpace="normal"
                                  >
                                    <HighlightedText
                                      html={result.highlightTitle}
                                      text={result.title}
                                      query={debouncedQuery}
                                    />
                                  </Text>
                                  <Badge
                                    size="sm"
                                    colorScheme={getEntityBadgeColor(result.entityType)}
                                    fontSize="xs"
                                  >
                                    {getEntityTypeName(result.entityType, t)}
                                  </Badge>
                                </HStack>

                                {result.description && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    <HighlightedText
                                      html={result.highlightDescription}
                                      text={result.description}
                                      query={debouncedQuery}
                                    />
                                  </Text>
                                )}

                                {result.contentPreview && !result.description && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    <HighlightedText
                                      text={result.contentPreview}
                                      query={debouncedQuery}
                                    />
                                  </Text>
                                )}

                                {metadataLine && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1} mt={0.5}>
                                    <HighlightedText text={metadataLine} query={debouncedQuery} />
                                  </Text>
                                )}
                              </Box>

                              <Icon
                                as={FiArrowRight}
                                color="gray.400"
                                boxSize={4}
                                opacity={index === selectedIndex ? 1 : 0}
                              />
                            </HStack>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </VStack>
                </Box>
              )}

              {hasResults && (
                <Box
                  px={3}
                  py={2}
                  textAlign="center"
                  borderTop="1px"
                  borderColor={borderColor}
                  cursor={hasMore ? 'pointer' : 'default'}
                  _hover={hasMore ? { bg: hoverBg } : undefined}
                  onClick={handleLoadMore}
                  role={hasMore ? 'button' : undefined}
                >
                  <HStack spacing={2} justify="center">
                    {isFetching && <Spinner size="xs" color="gray.400" />}
                    <Text fontSize="xs" color="gray.500">
                      {t('search.showingOf', {
                        count: results.length,
                        total: totalCount,
                      })}
                    </Text>
                  </HStack>
                </Box>
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
}
